import { Box, Typography, Button, Chip, ListItem, ListItemText, ListItemIcon, List, IconButton } from '@mui/material';
import { useEffect, useState, useMemo, useRef, useCallback } from 'react';
import { useSnackbar } from 'notistack';
import { useMutation } from 'react-query';
import { useLocation } from 'react-router-dom';

import AddItemField from 'src/widgets/AddItemField';
import Title from 'src/components/Title';
import Book from 'src/icons/Book';
import Trash from 'src/icons/Trash';
import OpenedBook from 'src/icons/OpenedBook';
import { AIIcon } from 'src/icons/AIIcon';
import { CaseIcon } from 'src/icons/CaseIcon';
import useUserStore from 'src/stores/user';
import useProjectsStore from 'src/stores/projects';
import LoadingWrapper from 'src/components/Loading/LoadingWrapper';
import ProjectsService from 'src/services/ProjectsService';
import useIsMobileSize from 'src/hooks/useIsMobileSize';

import type { IKeyword } from 'src/types/Keywords';

const EmptyList = () => {
    return (
        <Box
            display="flex"
            flexDirection="column"
            justifyContent="center"
            alignItems="center"
            flexGrow={10}
        >
            <Box mb={1}>
                <OpenedBook />
            </Box>
            <Box mb={0.5}>
                <Typography
                    variant="body1"
                    color="text.primary"
                    textAlign="center"
                >
                    Добавьте слова
                </Typography>
            </Box>
            <Box>
                <Typography
                    variant="body2"
                    color="secondary"
                    textAlign="center"
                >
                    Формы склонения и синонимы будут добавленны автоматически, для вашего удобства
                </Typography>
            </Box>
        </Box>
    );
};

export interface IKeywordsProps {
    onSuccess?: () => void;
    /** Флаг онбординга - если true, не показываем ошибки при отсутствии проекта */
    isOnboarding?: boolean;
}

export interface IKeywordState {
    id: number | string;
    name: string;
    isAIRecommended?: boolean;
}

// Нормализация ключевого слова для сравнения (case-insensitive)
const normalizeKeyword = (keyword: string): string => {
    return keyword.toLowerCase().trim();
};

// Генерация уникального ID для рекомендованных ключевых слов
let recommendedKeywordIdCounter = 0;
const generateRecommendedKeywordId = (): string => {
    recommendedKeywordIdCounter += 1;
    return `recommended-${Date.now()}-${recommendedKeywordIdCounter}`;
};

/**
 * Преобразует keywords проекта в структуру для отображения
 * Помечает слова как AI-рекомендованные, если они есть в niche_analysis_keywords
 */
const getKeywordsStructure = (keywords: IKeyword[], nicheAnalysisKeywords: string[] = []): IKeywordState[] => {
    const recommendedSet = new Set(nicheAnalysisKeywords.map((kw) => normalizeKeyword(kw)));

    return keywords.map(({ id, word }) => ({
        id,
        name: word,
        isAIRecommended: recommendedSet.has(normalizeKeyword(word)),
    }));
};

export default function Keywords({ onSuccess, isOnboarding = false }: IKeywordsProps) {
    const { enqueueSnackbar } = useSnackbar();
    const location = useLocation();
    const isMobile = useIsMobileSize();

    const { activeProject, setActiveProjectById, setActiveProject, projects, setProjects } = useProjectsStore(
        ({ activeProject, setActiveProjectById, setActiveProject, projects, setProjects }) => ({
            activeProject,
            setActiveProjectById,
            setActiveProject,
            projects,
            setProjects,
        })
    );

    // Получаем projectId из URL (приоритет) или из activeProject
    const projectIdFromUrl = useMemo(() => {
        const searchParams = new URLSearchParams(location.search);
        const param = searchParams.get('projectId');
        return param ? parseInt(param, 10) : null;
    }, [location.search]);

    // Флаг - есть ли projectId в URL (не онбординг режим)
    const hasProjectIdInUrl = projectIdFromUrl !== null && !isNaN(projectIdFromUrl);

    // Загружаем проекты и устанавливаем активный проект
    const [isLoadingProjects, setIsLoadingProjects] = useState(false);

    useEffect(() => {
        const loadProjects = async () => {
            // Если projectId из URL и он уже активен - ничего не делаем
            if (hasProjectIdInUrl && activeProject?.id === projectIdFromUrl) {
                return;
            }

            // Если projectId из URL, но проект другой или не загружен
            if (hasProjectIdInUrl) {
                const existingProject = projects.find((p) => p.id === projectIdFromUrl);
                if (existingProject) {
                    setActiveProjectById(projectIdFromUrl!);
                    return;
                }

                // Загружаем проекты
                setIsLoadingProjects(true);
                try {
                    const data = await ProjectsService.getProjects();
                    setProjects(data);
                    const targetProject = data.find((p) => p.id === projectIdFromUrl);
                    if (targetProject) {
                        setActiveProject(targetProject);
                    } else {
                        enqueueSnackbar('Проект не найден', { variant: 'error' });
                    }
                } catch {
                    enqueueSnackbar('Ошибка загрузки проектов', { variant: 'error' });
                } finally {
                    setIsLoadingProjects(false);
                }
                return;
            }

            // Онбординг режим - нет projectId в URL
            // Если есть активный проект - используем его
            if (activeProject?.id) {
                return;
            }

            // Если есть проекты, устанавливаем первый как активный
            if (projects.length > 0) {
                setActiveProject(projects[0]);
                return;
            }

            // Загружаем проекты
            setIsLoadingProjects(true);
            try {
                const data = await ProjectsService.getProjects();
                setProjects(data);
                if (data.length > 0) {
                    setActiveProject(data[0]);
                }
            } catch {
                // При онбординге не показываем ошибку
                if (!isOnboarding) {
                    enqueueSnackbar('Ошибка загрузки проектов', { variant: 'error' });
                }
            } finally {
                setIsLoadingProjects(false);
            }
        };

        loadProjects();
    }, [
        hasProjectIdInUrl,
        projectIdFromUrl,
        activeProject?.id,
        projects,
        setActiveProjectById,
        setActiveProject,
        setProjects,
        isOnboarding,
        enqueueSnackbar,
    ]);

    // Keywords из активного проекта
    const projectKeywords = useMemo((): IKeyword[] => {
        if (!activeProject?.keywords) {
            return [];
        }
        return activeProject.keywords.map((kw: any) => ({
            id: typeof kw.id === 'number' ? kw.id : 0,
            word: typeof kw === 'string' ? kw : kw.word,
        }));
    }, [activeProject?.keywords]);

    // Рекомендованные ключевые слова из анализа ниши
    const nicheKeywords = useMemo(() => {
        return (activeProject?.niche_analysis_keywords || []).filter((kw) => kw && kw.trim());
    }, [activeProject?.niche_analysis_keywords]);

    // Состояние для хранения ключевых слов (включая рекомендованные)
    const [items, setItems] = useState<IKeywordState[]>([]);

    // Отслеживаем удаленные пользователем рекомендованные ключевые слова
    // Map<projectId, Set<normalizedKeyword>>
    const deletedRecommendedRef = useRef<Map<number, Set<string>>>(new Map());

    // Флаг для отслеживания сохранения
    const justSavedRef = useRef(false);

    // Инициализируем items при изменении проекта или keywords
    useEffect(() => {
        if (justSavedRef.current) {
            return;
        }

        const projectId = activeProject?.id || 0;
        const deletedSet = deletedRecommendedRef.current.get(projectId) || new Set<string>();

        // Сохраненные keywords проекта (фильтруем удаленные)
        const savedKeywords = getKeywordsStructure(projectKeywords, nicheKeywords).filter(
            (kw) => !deletedSet.has(normalizeKeyword(kw.name))
        );

        const savedNames = new Set(savedKeywords.map((kw) => normalizeKeyword(kw.name)));

        // Рекомендованные слова, которых нет в сохраненных (и не удалены пользователем)
        const unsavedRecommended: IKeywordState[] = nicheKeywords
            .filter((kw) => {
                const norm = normalizeKeyword(kw);
                return !savedNames.has(norm) && !deletedSet.has(norm);
            })
            .map((kw) => ({
                id: generateRecommendedKeywordId(),
                name: kw.trim(),
                isAIRecommended: true,
            }));

        setItems([...savedKeywords, ...unsavedRecommended]);
    }, [projectKeywords, nicheKeywords, activeProject?.id]);

    // Лимит ключевых слов из подписки (общий на все проекты)
    const keywordsLimit = useUserStore(({ current_subscription }) => current_subscription?.plan.keywords_limit) || 0;
    const MAX_KEYWORDS_LIMIT = 100;

    // Эффективный лимит
    const effectiveLimit = useMemo(() => {
        if (keywordsLimit && keywordsLimit > 0) {
            return Math.min(keywordsLimit, MAX_KEYWORDS_LIMIT);
        }
        return MAX_KEYWORDS_LIMIT;
    }, [keywordsLimit]);

    // Количество keywords в других проектах (не текущем)
    const otherProjectsKeywordsCount = useMemo(() => {
        return projects.reduce((total, project) => {
            if (project.id === activeProject?.id) {
                return total; // Текущий проект не считаем — его keywords в items
            }
            return total + (project.keywords?.length || 0);
        }, 0);
    }, [projects, activeProject?.id]);

    // Общее количество: другие проекты + текущий список (items)
    const totalKeywordsCount = otherProjectsKeywordsCount + items.length;

    // Сколько ещё можно добавить в текущий проект
    const remainingSlots = Math.max(0, effectiveLimit - totalKeywordsCount);

    // Проверка достижения лимита
    const isMaxCount = effectiveLimit > 0 && remainingSlots === 0;

    const { mutate: updateProject, isLoading: isUpdateProjectLoading } = useMutation(
        useProjectsStore.getState().updateProject,
        {
            onSuccess: () => {
                justSavedRef.current = true;

                // Обновляем проекты после сохранения
                ProjectsService.getProjects()
                    .then((data) => {
                        setProjects(data);
                        if (activeProject?.id) {
                            setActiveProjectById(activeProject.id);
                        }
                        // Очищаем список удаленных слов для текущего проекта
                        if (activeProject?.id) {
                            deletedRecommendedRef.current.delete(activeProject.id);
                        }
                        setTimeout(() => {
                            justSavedRef.current = false;
                        }, 100);
                    })
                    .catch(() => {
                        justSavedRef.current = false;
                    });

                enqueueSnackbar('Ключевые слова сохранены', { variant: 'success' });
                onSuccess?.();
            },
            onError: (error) => {
                console.error('Ошибка сохранения ключевых слов:', error);
                enqueueSnackbar('Ошибка сохранения ключевых слов', { variant: 'error' });
                justSavedRef.current = false;
            },
        }
    );

    const onAddKeyword = useCallback(
        (value: string) => {
            const normalizedValue = value.trim();
            if (!normalizedValue) {
                enqueueSnackbar('Ключевое слово не может быть пустым', { variant: 'warning' });
                return;
            }

            setItems((prevState) => {
                // Проверка дубликатов (case-insensitive)
                if (prevState.some(({ name }) => normalizeKeyword(name) === normalizeKeyword(normalizedValue))) {
                    enqueueSnackbar('Ключевое слово уже добавлено!', { variant: 'info' });
                    return prevState;
                }

                // Проверяем общий лимит ключевых слов (другие проекты + текущий список + 1)
                const newTotal = otherProjectsKeywordsCount + prevState.length + 1;
                if (effectiveLimit > 0 && newTotal > effectiveLimit) {
                    enqueueSnackbar(`Достигнут лимит ключевых слов (${effectiveLimit} на все проекты)`, {
                        variant: 'warning',
                    });
                    return prevState;
                }

                // Генерируем уникальный ID для нового слова
                const maxId = prevState.reduce((max, item) => {
                    const numId = typeof item.id === 'number' ? item.id : 0;
                    return Math.max(max, numId);
                }, 0);

                return [...prevState, { name: normalizedValue, id: maxId + 1, isAIRecommended: false }];
            });
        },
        [effectiveLimit, otherProjectsKeywordsCount, enqueueSnackbar]
    );

    const onDeleteKeyword = useCallback(
        (keyword: IKeywordState, event?: React.MouseEvent) => {
            if (event) {
                event.preventDefault();
                event.stopPropagation();
            }

            const normalizedName = normalizeKeyword(keyword.name);
            const projectId = activeProject?.id || 0;

            // Проверяем, есть ли это слово в рекомендованных
            const isInRecommended = nicheKeywords.some((kw) => normalizeKeyword(kw) === normalizedName);

            // Удаляем по ID
            setItems((prevState) => prevState.filter((item) => item.id !== keyword.id));

            // Если слово рекомендованное - запоминаем удаление, чтобы не показывать снова
            if ((keyword.isAIRecommended || isInRecommended) && projectId > 0) {
                const projectSet = deletedRecommendedRef.current.get(projectId) || new Set<string>();
                projectSet.add(normalizedName);
                deletedRecommendedRef.current.set(projectId, projectSet);
            }
        },
        [activeProject?.id, nicheKeywords]
    );

    const onSaveHandler = useCallback(() => {
        if (!activeProject?.id) {
            enqueueSnackbar('Для сохранения необходимо выбрать проект', { variant: 'warning' });
            return;
        }

        // Уникальные непустые ключевые слова
        const uniqueKeywords = Array.from(
            new Set(items.map(({ name }) => name.trim()).filter((name) => name.length > 0))
        );

        // Проверяем общий лимит (другие проекты + то что сохраняем)
        // Но разрешаем сохранение, если пользователь уменьшает количество слов в текущем проекте
        const currentProjectSavedCount = activeProject?.keywords?.length || 0;
        const newTotal = otherProjectsKeywordsCount + uniqueKeywords.length;
        const isReducing = uniqueKeywords.length <= currentProjectSavedCount;

        if (effectiveLimit > 0 && newTotal > effectiveLimit && !isReducing) {
            enqueueSnackbar(`Превышен лимит ключевых слов (${effectiveLimit} на все проекты)`, {
                variant: 'warning',
            });
            return;
        }

        justSavedRef.current = true;

        updateProject({
            id: activeProject.id,
            keywords: uniqueKeywords,
        });
    }, [activeProject?.id, items, effectiveLimit, otherProjectsKeywordsCount, updateProject, enqueueSnackbar]);

    return (
        <LoadingWrapper
            isLoading={isUpdateProjectLoading || isLoadingProjects}
            isForced={isLoadingProjects}
        >
            <Box
                pt={2}
                minHeight="100%"
                display="flex"
                flexDirection="column"
            >
                <Box
                    mb={1.5}
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                    gap={2}
                >
                    <Title>Что искать?</Title>
                    <Box
                        display="flex"
                        alignItems="center"
                        gap={1}
                    >
                        {hasProjectIdInUrl && activeProject?.name && (
                            <Chip
                                icon={<CaseIcon />}
                                label={activeProject.name}
                                size="medium"
                                sx={{
                                    backgroundColor: 'rgba(25, 118, 210, 0.1)',
                                    color: 'text.primary',
                                    fontWeight: 600,
                                    border: '1px solid rgba(25, 118, 210, 0.2)',
                                    paddingLeft: '8px',
                                    paddingRight: '8px',
                                    gap: '8px',
                                    '& .MuiChip-icon': {
                                        color: 'text.primary',
                                        marginLeft: 0,
                                        marginRight: 0,
                                    },
                                }}
                            />
                        )}
                        {effectiveLimit > 0 && (
                            <Chip
                                color="primary"
                                variant={totalKeywordsCount >= effectiveLimit ? 'filled' : 'outlined'}
                                label={`${totalKeywordsCount} из ${effectiveLimit}`}
                                sx={{
                                    background:
                                        totalKeywordsCount < effectiveLimit && !isMobile ? 'var(--lm-surface)' : null,
                                }}
                            />
                        )}
                    </Box>
                </Box>
                <Box
                    maxWidth="80%"
                    mb={20 / 8}
                >
                    <Typography
                        variant="body1"
                        color="text.primary"
                    >
                        Добавляйте ключевые слова и фразы, по которым клиенты могут искать ваши продукты или услуги
                    </Typography>
                </Box>
                <Box mb={3}>
                    <AddItemField
                        icon={<Book />}
                        placeholder={isMaxCount ? `Достигнут лимит (${effectiveLimit})` : 'Добавьте ключевое слово'}
                        onAddItem={onAddKeyword}
                        disabled={isMaxCount}
                    />
                </Box>
                {items.length === 0 ? (
                    <EmptyList />
                ) : (
                    <List
                        style={{
                            border: '1px solid',
                            borderColor: 'var(--lm-border)',
                            borderRadius: '16px',
                            backgroundColor: 'var(--lm-surface)',
                            width: '100%',
                        }}
                        disablePadding
                    >
                        {items.map((entity, idx) => (
                            <ListItem
                                key={entity.id}
                                divider={items.length - 1 !== idx}
                                disablePadding={false}
                                secondaryAction={
                                    <IconButton
                                        edge="end"
                                        aria-label="delete"
                                        onClick={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            onDeleteKeyword(entity, e);
                                        }}
                                        onMouseDown={(e) => {
                                            // Дополнительная защита от всплытия
                                            e.preventDefault();
                                            e.stopPropagation();
                                        }}
                                        type="button"
                                        sx={{
                                            pointerEvents: 'auto',
                                        }}
                                    >
                                        <Trash />
                                    </IconButton>
                                }
                                sx={{
                                    '&:hover': {
                                        backgroundColor: 'transparent',
                                    },
                                }}
                            >
                                <ListItemIcon>
                                    <Box
                                        sx={{
                                            '& svg': {
                                                width: '20px',
                                                height: '20px',
                                            },
                                        }}
                                    >
                                        <Book />
                                    </Box>
                                </ListItemIcon>
                                <ListItemText
                                    primary={
                                        <Box
                                            display="flex"
                                            alignItems="center"
                                            gap={1}
                                        >
                                            <Typography variant="body1">{entity.name}</Typography>
                                            {entity.isAIRecommended && (
                                                <Chip
                                                    icon={
                                                        <AIIcon
                                                            width={14}
                                                            height={14}
                                                        />
                                                    }
                                                    label="ИИ рекомендует"
                                                    size="small"
                                                    sx={{
                                                        backgroundColor: 'rgba(122, 48, 228, 0.1)',
                                                        color: '#7A30E4',
                                                        fontWeight: 600,
                                                        border: '1px solid rgba(122, 48, 228, 0.2)',
                                                        '& .MuiChip-icon': {
                                                            color: '#7A30E4',
                                                        },
                                                    }}
                                                />
                                            )}
                                        </Box>
                                    }
                                />
                            </ListItem>
                        ))}
                    </List>
                )}
                <Box
                    display="flex"
                    alignItems="flex-end"
                    flexGrow={1}
                    pb={1}
                >
                    <Button
                        variant="contained"
                        color="primary"
                        size="large"
                        fullWidth
                        onClick={onSaveHandler}
                        disabled={false}
                    >
                        Сохранить
                    </Button>
                </Box>
            </Box>
        </LoadingWrapper>
    );
}
